import React, {useMemo} from "react";
import {useKeyDataStore} from "../../../api/KeyDataStore";
import {KeyProblems} from "../../../api/KeyProblemStore";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {KeyLocationShield, toLocationTypeEnum} from "../../../components/location/KeyLocationShield";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Link as RouterLink} from "react-router-dom";
import {KeyChip} from "../../../components/key-chip/KeyChip";
import {Box, Typography} from "@mui/material";
import {KeyHistoryPeriodEnd} from "../../../components/key-history-period/KeyHistoryPeriod";
import {EmployeeHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {KeyHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {KeyData_Read} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {KeySnapshot} from "../../../api/Model";
import {EmployeeMap, useEmployeeMapStore} from "../../../api/EmployeeMapStore";


class NotReturnedKeysCsvModel extends CsvModel<KeySnapshot> {
    constructor(employeeMap: EmployeeMap) {
        super([
            {
                header: 'Key',
                value: ({key}) => key.keyNumber,
            },
            {
                header: 'Company',
                value: ({historyRecord: {location}}) => location.type === 'with-guest' ? (
                    location.company ?? ''
                ) : '',
            },
            {
                header: 'Person',
                value: ({historyRecord: {location}}) => location.type === 'with-employee' ? (
                    location.employee.fullName
                ) : location.type === 'with-guest' ? (
                    location.fullName
                ) : '',
            },
            {
                header: 'Email',
                value: ({historyRecord: {location}}) => location.type === 'with-employee' ? (
                    employeeMap.get(location.employee)?.email ?? ''
                ) : '',
            },
            {
                header: 'Expected Date',
                value: ({historyRecord: {location}}) => location.type === 'with-employee' || location.type === 'with-guest' ? (
                    location.expTo ?? ''
                ) : '',
            }
        ]);
    }
}

export function NotReturnedKeysWidget() {
    const pageUrl = usePageUrl();
    const keys = useKeyDataStore(state => state.remoteValue);
    const employeeMap = useEmployeeMapStore(state => state.remoteValue);
    const problems = useMemo(() => keys.map(items => {
        const keyProblems = new KeyProblems(items);
        return items.flatMap<KeySnapshot>(key => {
            if (!keyProblems.hasProblem(key, 'not-returned-key')) return [];
            const historyRecord = key.history.find(record => !record.to && toLocationTypeEnum(record.location).expectedReturnDate);
            return historyRecord ? [{key, historyRecord}] : [];
        });
    }), [keys]);
    const csvModel = useMemo(() => employeeMap.map(
        employeeMap => new NotReturnedKeysCsvModel(employeeMap)
    ), [employeeMap]);

    return (
        <DashboardWidget
            title="Not returned keys"
            description="Keys that have been issued with expected return date but not returned by that date"
            problems={problems}
            requiredPermissions={KeyData_Read}
            link={pageUrl(AppPages.keys, {}, {problem: 'not-returned-key'})}
            csvModel={csvModel}
            renderProblem={({key, historyRecord}, index) => {
                return (
                    <DashboardWidgetItem
                        key={key.id}
                        index={index}
                        left={
                            <>
                                <Box sx={{minWidth: '96px'}}>
                                    <RouterLink to={pageUrl(AppPages.keys, {}, {search: key.keyNumber})}>
                                        <KeyChip keyData={key} record={historyRecord}/>
                                    </RouterLink>
                                </Box>
                                <KeyLocationShield location={historyRecord.location}/>
                                {historyRecord.location.type === 'with-employee' && (
                                    <EmployeeHistoryButton employee={historyRecord.location.employee}/>
                                )}
                            </>
                        }
                        right={
                            <>
                                <Typography variant="body2">
                                    <KeyHistoryPeriodEnd record={historyRecord}/>
                                </Typography>
                                <KeyHistoryButton keyData={key}/>
                            </>
                        }
                    />
                );
            }}
        />
    );
}
